import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Filter, X, Sparkles, Clock, Stars } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function UnifiedSearchPanel({
  isClient = false,
  search = '',
  setSearch,
  categories = [],
  selectedCategory = '',
  setSelectedCategory,
  fileTypeFilter = '',
  setFileTypeFilter,
  sortBy = 'date',
  setSortBy,
  sortOrder = 'desc',
  setSortOrder,
  fileTypeOptions = [{ value: '', label: 'All Types' }],
  showFilters = true,
}) {
  const [searchQuery, setSearchQuery] = useState(search);
  const [recentSearches, setRecentSearches] = useState([]);
  const debounceTimer = useRef(null);

  // Sync searchQuery with external search prop
  useEffect(() => {
    setSearchQuery(search);
  }, [search]);

  // hydrate recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('document-bank-recent-searches');
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (_) {
      // graceful fallback if storage unavailable
    }
  }, []);

  // persist recent searches
  useEffect(() => {
    try {
      localStorage.setItem('document-bank-recent-searches', JSON.stringify(recentSearches));
    } catch (_) {}
  }, [recentSearches]);

  // Debounced search update
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setSearch(searchQuery.trim());
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, setSearch]);

  const handleSearchChange = e => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    const trimmed = searchQuery.trim();
    setSearch(trimmed);
    if (trimmed) {
      setRecentSearches(prev => {
        const next = [trimmed, ...prev.filter(item => item !== trimmed)].slice(0, 6);
        return next;
      });
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearch('');
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  };

  const hasActiveFilters =
    searchQuery || selectedCategory || fileTypeFilter || sortBy !== 'date' || sortOrder !== 'desc';

  const quickCategories = categories.slice(0, 4);
  const quickTypes = fileTypeOptions.filter(opt => opt.value).slice(0, 4);
  const trending = ['pdf', 'contract', 'invoice', 'proposal'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative mb-6 overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-br from-white via-blue-50/40 to-white shadow-xl shadow-blue-200/30 ring-1 ring-blue-100"
    >
      <div className="pointer-events-none absolute inset-x-6 top-4 h-24 rounded-3xl bg-white/40 blur-3xl" />

      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-4 py-4 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/30"
            >
              <Search className="h-5 w-5" />
            </motion.div>
            <div>
              <h3 className="text-lg font-bold text-black">Search & Filter</h3>
              <p className="text-xs font-medium text-gray-600">
                Find documents quickly with advanced options
              </p>
            </div>
          </div>
          <div className="hidden items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-blue-700 shadow-sm ring-1 ring-blue-100 sm:flex">
            <Stars className="h-3.5 w-3.5" />
            <span>Refreshed look</span>
          </div>
          <AnimatePresence>
            {hasActiveFilters && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  handleClearSearch();
                  setSelectedCategory('');
                  setFileTypeFilter('');
                  setSortBy('date');
                  setSortOrder('desc');
                }}
                className="shrink-0 rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-black shadow-sm transition-all hover:bg-gray-50"
              >
                Clear All
              </motion.button>
            )}
          </AnimatePresence>
        </div>
        {hasActiveFilters && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-600">
            {searchQuery && (
              <span className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-800">
                <Search className="h-3 w-3" /> {searchQuery}
              </span>
            )}
            {selectedCategory && (
              <span className="flex items-center gap-1 rounded-full bg-indigo-100 px-3 py-1 font-semibold text-indigo-800">
                Category: {selectedCategory}
              </span>
            )}
            {fileTypeFilter && (
              <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-800">
                Type set
              </span>
            )}
            {(sortBy !== 'date' || sortOrder !== 'desc') && (
              <span className="flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-800">
                Sorting updated
              </span>
            )}
          </div>
        )}
      </div>

      {/* Search Input */}
      <div className="px-4 py-4 sm:px-6 sm:py-5">
        <div className="group relative">
          <div className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 sm:left-5">
            <Search className="h-5 w-5 text-gray-400 transition-colors duration-200 group-focus-within:text-blue-600" />
          </div>

          <motion.input
            whileFocus={{ scale: 1.01 }}
            transition={{ type: 'spring', stiffness: 300 }}
            type="text"
            className="w-full rounded-2xl border-2 border-transparent bg-white/80 py-3 pl-12 pr-12 text-sm font-semibold text-black shadow-lg shadow-blue-100/50 backdrop-blur-md transition-all duration-200 placeholder:font-normal placeholder:text-gray-400 hover:border-blue-100 hover:bg-white focus:border-blue-500 focus:bg-white focus:shadow-blue-200/80 focus:outline-none focus:ring-4 focus:ring-blue-500/20 sm:py-4 sm:pl-14 sm:pr-14"
            placeholder={
              isClient
                ? 'Search your documents by name, type, or notes...'
                : 'Search all documents by name, type, or notes...'
            }
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                handleClearSearch();
              } else if (e.key === 'Enter') {
                handleSearchSubmit();
              }
            }}
          />
          <AnimatePresence>
            {searchQuery && (
              <motion.button
                type="button"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClearSearch}
                className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-lg bg-gray-200 p-2 text-gray-600 transition-colors hover:bg-gray-300 hover:text-gray-800"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
            <Clock className="h-3.5 w-3.5" />
            Smart search
          </span>
          {trending.map(tag => (
            <button
              key={tag}
              onClick={() => {
                setSearchQuery(tag);
                setSearch(tag);
              }}
              className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm transition-all hover:-translate-y-[1px] hover:bg-blue-50 hover:text-blue-700 hover:shadow"
            >
              #{tag}
            </button>
          ))}
        </div>
        <AnimatePresence>
          {searchQuery && searchQuery !== search && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="mt-2 flex items-center gap-2 text-xs"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className="h-3 w-3 text-blue-600" />
              </motion.div>
              <span className="font-medium text-gray-600">
                Press{' '}
                <kbd className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs">Enter</kbd> to
                search or wait for auto-search...
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        {!!recentSearches.length && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {recentSearches.map(item => (
              <button
                key={item}
                onClick={() => {
                  setSearchQuery(item);
                  setSearch(item);
                }}
                className="rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-700"
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filters Section */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-gray-200 bg-gradient-to-br from-white to-gray-50 px-6 py-5"
        >
          <div className="mb-4 flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-600" />
            <h4 className="text-sm font-bold text-black">Filter Options</h4>
          </div>
          {(quickCategories.length > 0 || quickTypes.length > 0) && (
            <div className="mb-4 flex flex-wrap gap-2">
              {quickCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${
                    selectedCategory === cat
                      ? 'bg-blue-600 text-white shadow-sm shadow-blue-200'
                      : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-blue-50 hover:text-blue-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
              {quickTypes.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFileTypeFilter(opt.value)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${
                    fileTypeFilter === opt.value
                      ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-200'
                      : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-emerald-50 hover:text-emerald-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-700">
                Category
              </label>
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  className="w-full appearance-none rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 pr-10 text-sm font-medium text-black shadow-sm transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-700">
                File Type
              </label>
              <div className="relative">
                <select
                  value={fileTypeFilter}
                  onChange={e => setFileTypeFilter(e.target.value)}
                  className="w-full appearance-none rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 pr-10 text-sm font-medium text-black shadow-sm transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                >
                  {fileTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-700">
                Sort By
              </label>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="w-full appearance-none rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 pr-10 text-sm font-medium text-black shadow-sm transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                >
                  <option value="date">Upload Date</option>
                  <option value="name">File Name</option>
                  <option value="size">File Size</option>
                  <option value="type">File Type</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-700">
                Order
              </label>
              <div className="relative">
                <select
                  value={sortOrder}
                  onChange={e => setSortOrder(e.target.value)}
                  className="w-full appearance-none rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 pr-10 text-sm font-medium text-black shadow-sm transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
